import { api_configuration } from "../../common";
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { gregorian_date_2_julian_date, gregorian_datetime_2_julian_datetime } from "../calendar/conversion";
import Gregorian_Datetime from "../calendar/Gregorian_Datetime";
import Gregorian_Date from "../calendar/Gregorian_Date";
import Hour_Time from "../calendar/Hour_Time";
import { gui_model } from "../state/ClientState";

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;

export function parseHours(duration: string): number {
	return Math.floor(parseSeconds(duration) / SECONDS_PER_HOUR);
}

export function parseMinutes(duration: string): number {
	return Math.floor(parseSeconds(duration) / SECONDS_PER_MINUTE);
}

export function parseSeconds(secondsstring: string): number {
	const res = /^(-)?(([0-9]+)h)?(([0-9]+)m)?(([0-9]+)s?)?$/.exec(secondsstring);
	return res === null ||
		(res[1] === undefined &&
			res[3] === undefined &&
			res[5] === undefined &&
			res[7] === undefined &&
			res[9] === undefined)
		? NaN
		: (res[1] === undefined ? 1 : -1) *
				((res[3] === undefined ? 0 : parseInt(res[3], 10)) * SECONDS_PER_HOUR +
					(res[5] === undefined ? 0 : parseInt(res[5], 10)) * SECONDS_PER_MINUTE +
					(res[7] === undefined ? 0 : parseInt(res[7], 10)));
}

function parseDate(datestring: string): number {
	const res = /^(\d+)-(\d+)-(\d+)$/.exec(datestring);
	if (res === null) {
		return NaN;
	}
	const year = parseInt(res[1], 10);
	const month = parseInt(res[2], 10);
	const day = parseInt(res[3], 10);
	if (month < 1 || month > 12 || day < 1 || day > 31) {
		return NaN;
	}
	return gregorian_date_2_julian_date(new Gregorian_Date(year, month, day));
}

export function parseDateTime(datetimestring: string): number {
	const res = /^(\d+)-(\d+)-(\d+)(\s|\w)(\d+):(\d+):(\d+)$/.exec(datetimestring);
	if (res === null) {
		return NaN;
	}
	const year = parseInt(res[1], 10);
	const month = parseInt(res[2], 10);
	const day = parseInt(res[3], 10);
	const hour = parseInt(res[5], 10);
	const minutes = parseInt(res[6], 10);
	const seconds = parseInt(res[7], 10);
	if (
		month < 1 ||
		month > 12 ||
		day < 1 ||
		day > 31 ||
		hour < 0 ||
		hour > 24 ||
		minutes < 0 ||
		minutes > 60 ||
		seconds < 0 ||
		seconds > 60
	) {
		return NaN;
	}
	return gregorian_datetime_2_julian_datetime(
		new Gregorian_Datetime(new Gregorian_Date(year, month, day), new Hour_Time(hour, minutes, seconds))
	);
}
type NumberParseResult = ["no", { error: string }] | ["yes", { number: number }];
function convertToParseResult(value: number, error: string): NumberParseResult {
	return isNaN(value)
		? [
				"no",
				{
					error: error,
				},
		  ]
		: [
				"yes",
				{
					number: value,
				},
		  ];
}

const DECIMAL_SEPARATOR = (1.1)
	.toLocaleString(window.navigator.language || window.navigator.languages[0])
	.substring(1, 2);
const THOUSAND_SEPARATOR = (1000)
	.toLocaleString(window.navigator.language || window.navigator.languages[0])
	.substring(1, 2)
	.replace("0", "");
const NUMBER_REGEX = new RegExp(
	`^(?<sign>-)?(?:(?<major1>[0-9 ${THOUSAND_SEPARATOR}]+${DECIMAL_SEPARATOR}?)|(?:(?<major2>[0-9 ${THOUSAND_SEPARATOR}]*)${DECIMAL_SEPARATOR}(?<minor>[0-9]+)))$`
);
const THOUSAND_SEPARATOR_REGEX = new RegExp(`[${THOUSAND_SEPARATOR}]`);
export function parse(
	numberstring: string,
	numerical_type: api_app.Cnumerical_types,
	set_type: "integer" | "positive",
	point_translation: number
): NumberParseResult {
	const gui_numerical_type = gui_model.root.properties.numerical_types.get(numerical_type.key);
	const gui_result = gui_numerical_type?.properties.representation_type.switch({
		date: () => convertToParseResult(parseDate(numberstring), "expected format yyyy-mm-dd"),
		"date and time": () => convertToParseResult(parseDateTime(numberstring), "expected format yyyy-mm-dd h:m:s"),
		"HTML date and time": () => convertToParseResult(parseDateTime(numberstring), "expected format yyyy-mm-dd h:m:s"),
		model: () => undefined,
		duration: (duration) =>
			duration.properties.unit.switch({
				hours: () =>
					convertToParseResult(parseHours(numberstring), "expected format [#h][#m][#s] (e.g. 5h, 1h30s or 30m)"),
				minutes: () =>
					convertToParseResult(parseMinutes(numberstring), "expected format [#h][#m][#s] (e.g. 5h, 1h30s or 30m)"),
				seconds: () =>
					convertToParseResult(parseSeconds(numberstring), "expected format [#h][#m][#s] (e.g. 5h, 1h30s or 30m)"),
			}),
	});
	if (gui_result !== undefined) {
		return gui_result;
	} else {
		const match = numberstring.match(NUMBER_REGEX);
		if (match === null || match.groups === undefined) {
			return [
				"no",
				{
					error: "value is not a number",
				},
			];
		}
		const minor_string = match.groups["minor"];
		if (minor_string !== undefined && minor_string.length > point_translation) {
			return [
				"no",
				{
					error: `expected max ${point_translation} decimals`,
				},
			];
		}
		let minor = 0;
		if (minor_string !== undefined) {
			minor = parseInt(minor_string);
			const decimal_point_modification = point_translation - minor_string.length;
			if (decimal_point_modification > 0) {
				minor = minor * Math.pow(10, decimal_point_modification);
			}
		}
		const sign = match.groups["sign"] === undefined ? 1 : -1;
		let major = 0;
		let major_string = match.groups["major1"] ?? match.groups["major2"];
		if (major_string !== undefined) {
			major_string = major_string.replace(THOUSAND_SEPARATOR_REGEX, "");
			if (major_string.length > 0) {
				major = parseInt(major_string);
			}
		}
		const number_value = sign * (major * Math.pow(10, point_translation) + minor);
		switch (set_type) {
			case "positive":
				if (number_value <= 0) {
					return [
						"no",
						{
							error: "expected value > 0",
						},
					];
				}
				break;
			case "integer":
				break;
			default:
				unexpectedState(set_type);
		}
		return [
			"yes",
			{
				number: number_value,
			},
		];
	}
}
